<template>
  <div class="field-file-edit position-relative" :class="{ 'readonly-field': calculated }">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <!-- Campo de archivo simple (no array) -->
    <div v-if="!field.isArray" class="d-flex align-items-center gap-2">
      <div class="flex-grow-1">
        <input
          ref="fileInput"
          type="file"
          v-show="false"
          class="form-control"
          :class="{ 'border-primary': file }"
          style="background: none"
          :id="computedFieldId"
          :accept="effectiveAccept"
          @change="onFileChange"
          :required="field.required"
          :disabled="field.readonly || isLocked"
        />
        <small v-if="file">
          {{ file.name }}
        </small>
        <small v-if="isUploading && !file" class="text-primary">
          {{ $t('Uploading file...') }}
        </small>
        <div v-if="isUploading" class="mt-2">
          <small class="text-primary">
            <span
              class="spinner-border spinner-border-sm me-1"
              role="status"
            ></span>
            {{ $t('Uploading file...') }}
          </small>
        </div>
        <div class="mt-2 d-flex align-items-center gap-2">
          <small
            ><i class="material-icons text-primary" v-if="url">attach_file</i>
            <a
              v-if="url"
              href="#"
              @click.prevent="openFile"
              class="text-primary text-decoration-none"
            >
              {{ getFileName(url) }}
            </a></small
          >
          <button
            v-if="url || file"
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="deleteFile"
            :disabled="field.readonly || isLocked"
            :title="$t('delete file')"
          >
            <i class="material-icons">delete</i>
          </button>
          <button
            v-if="url"
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :title="$t('change file')"
            @click="triggerFileInput"
            :disabled="field.readonly || isLocked"
          >
            <i class="material-icons">edit</i>
          </button>
          <button
            v-if="!url && !file"
            type="button"
            class="btn btn-sm btn-outline-primary"
            :title="$t('upload file')"
            @click="triggerFileInput"
            :disabled="field.readonly || isLocked"
          >
            <i class="material-icons">upload</i> {{ $t('Upload file') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Array de archivos (no común, pero soportado) -->
    <div v-else-if="field.isArray" class="file-array">
      <div
        v-for="(fileItem, index) in arrayValue"
        :key="index"
        class="file-array-item mb-2"
      >
        <div class="d-flex align-items-center gap-2">
          <small v-if="fileItem">
            <i class="material-icons text-primary">attach_file</i>
            <a
              href="#"
              @click.prevent="openFileAtIndex(index)"
              class="text-primary text-decoration-none"
            >
              {{ getFileName(fileItem) }}
            </a>
          </small>
          <button
            v-if="!field.readonly && !isLocked"
            type="button"
            class="btn btn-sm btn-danger"
            @click="removeArrayItem(index)"
          >
            <i class="material-icons">delete</i>
          </button>
        </div>
      </div>
      <input
        :ref="`fileInputArray_${index}`"
        v-for="(item, index) in arrayValue"
        :key="`input_${index}`"
        type="file"
        v-show="false"
        :accept="effectiveAccept"
        @change="onArrayFileChange(index, $event)"
        :disabled="field.readonly || isLocked"
      />
      <button
        v-if="!field.readonly && !isLocked"
        type="button"
        class="btn btn-sm btn-secondary"
        @click="addArrayItem"
      >
        <i class="material-icons">add</i> {{ $t('add') }}
      </button>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  isDocumentUploadAllowed,
  resolveDocumentFieldAccept,
} from "@/utils/documentUpload";

export default {
  name: "EditFieldFile",
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      file: null,
      url: null,
      isUploading: false,
      arrayValue: [],
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (this.field.isArray) {
          if (Array.isArray(newVal)) {
            this.arrayValue = [...newVal];
          } else if (typeof newVal === "string" && newVal.trim()) {
            this.arrayValue = [newVal.trim()];
          } else {
            this.arrayValue = [];
          }
        } else {
          if (typeof newVal === "string" && newVal) {
            this.url = newVal;
            this.file = null;
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    computedFieldId() {
      return this.fieldId || `field-file-${this.field.key}`;
    },
    isLocked() {
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
    effectiveAccept() {
      return resolveDocumentFieldAccept(this.field.accept);
    },
  },
  methods: {
    getFileName(url) {
      if (!url) return "";
      try {
        if (!url.startsWith("http")) {
          const parts = url.split("_");
          if (parts.length > 1) {
            return parts.slice(1).join("_");
          }
          return url;
        }
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const fileName = pathname.split("/").pop();
        return decodeURIComponent(fileName);
      } catch (e) {
        return url;
      }
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!this.validateDocumentFile(file, event.target)) return;
      this.file = file;
      this.uploadFile();
    },
    onArrayFileChange(index, event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!this.validateDocumentFile(file, event.target)) return;
      this.uploadArrayFile(index, file);
    },
    validateDocumentFile(file, inputEl) {
      if (isDocumentUploadAllowed(file)) return true;
      alert(this.$t("Document upload type not allowed"));
      if (inputEl) inputEl.value = "";
      this.file = null;
      return false;
    },
    setUploading(next) {
      this.isUploading = !!next;
      this.$emit("uploading", this.isUploading);
    },
    deleteFile() {
      this.url = null;
      this.file = null;
      this.$emit("input", null);
    },
    async uploadFile() {
      if (!this.file) return;
      try {
        this.setUploading(true);
        const formData = new FormData();
        formData.append("image", this.file);
        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        const fileKey = response.data.fileKey;
        this.url = fileKey;
        this.file = null;
        this.$emit("input", fileKey);
      } catch (error) {
        console.error("Error al subir el archivo:", error);
        alert(this.$t("Error uploading file. Please try again."));
      } finally {
        this.setUploading(false);
      }
    },
    async uploadArrayFile(index, file) {
      try {
        this.setUploading(true);
        const formData = new FormData();
        formData.append("image", file);
        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        const fileKey = response.data.fileKey;
        const newArray = [...this.arrayValue];
        newArray[index] = fileKey;
        this.emitArrayValue(newArray);
      } catch (error) {
        console.error("Error al subir el archivo:", error);
        alert(this.$t("Error uploading file. Please try again."));
      } finally {
        this.setUploading(false);
      }
    },
    async getPresignedUrl(fileKey) {
      try {
        let cleanFileKey = fileKey;
        if (fileKey.startsWith("fileuploads/")) {
          cleanFileKey = fileKey.replace("fileuploads/", "");
        }
        const response = await wencesApi.get(
          `/api/upload/file/${cleanFileKey}`
        );
        return response.data.url;
      } catch (error) {
        console.error("Error al obtener URL firmada:", error);
        return null;
      }
    },
    async openFile() {
      if (!this.url) return;
      if (!this.url.startsWith("http")) {
        const presignedUrl = await this.getPresignedUrl(this.url);
        if (presignedUrl) {
          window.open(presignedUrl, "_blank");
        } else {
          alert(this.$t("Error accessing file."));
        }
      } else {
        window.open(this.url, "_blank");
      }
    },
    async openFileAtIndex(index) {
      const fileKey = this.arrayValue[index];
      if (!fileKey) return;
      if (!fileKey.startsWith("http")) {
        const presignedUrl = await this.getPresignedUrl(fileKey);
        if (presignedUrl) {
          window.open(presignedUrl, "_blank");
        } else {
          alert(this.$t("Error accessing file."));
        }
      } else {
        window.open(fileKey, "_blank");
      }
    },
    triggerFileInput() {
      const fileInput = this.$refs.fileInput;
      if (fileInput) {
        fileInput.click();
      }
    },
    addArrayItem() {
      const newArray = [...this.arrayValue];
      newArray.push(null);
      this.arrayValue = newArray;
      this.$nextTick(() => {
        const inputRef = this.$refs[`fileInputArray_${newArray.length - 1}`];
        if (inputRef && inputRef[0]) {
          inputRef[0].click();
        }
      });
    },
    emitArrayValue(nextArray) {
      const cleaned = (Array.isArray(nextArray) ? nextArray : []).filter(
        (entry) => entry !== null && entry !== undefined && entry !== ""
      );
      this.arrayValue = cleaned;
      this.$emit("input", cleaned);
    },
    removeArrayItem(index) {
      const newArray = [...this.arrayValue];
      newArray.splice(index, 1);
      this.emitArrayValue(newArray);
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-file-edit {
  width: 100%;
}

.file-array-item {
  display: flex;
  align-items: center;
}

</style>

